import classNames from 'classnames';
import {
  Form, Formik, FormikProps,
} from 'formik';
import React, {
  FC, useEffect, useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { AddArticleRequestFormArticleProviderSelector } from './addArticleRequestFormArticleProviderSelector';
import { AddArticleRequestFormArticleSelector } from './addArticleRequestFormArticleSelector';
import { AddArticleRequestFormComponent } from './types';
import Datepicker from 'components/base/Datepicker';

import Input from 'components/base/Input';
import Select from 'components/base/Select';

import {
  MasterDataDto, OrderLine, OrderLineChannel,
} from 'config/apiFunus/generated/data-contracts';
import { useAdvice } from 'hooks/useAdvice';
import { useArticles } from 'hooks/useArticles';
import { useValidator } from 'hooks/useValidator';
import { priceFormatWithoutCurrency } from 'modules/budget/components/budgetSummary/tools';
import { formatHour, getCurrentDate } from 'utils/dateManager';

export const AddArticleRequestForm: FC<AddArticleRequestFormComponent> = (
  {
    channel,
    erpId,
    id,
    value,
    onArticleChange,
    onChange,
    onIsValidUpdate,
    onSupplierChange,
    vigilDate,
    vigilHour,
    articleTypeDisabled,
    articleDisabled,
    priceDisabled,
    providerDisabled,
    clientDisabled,
    showClientSelector,
    showCategorySelector,
    showArticleDescription,
    showArticleSelector,
    isGift = false,
    className,
  },
) => {
  const { t } = useTranslation();
  const { validateArticleAddRequest, validateArticleGift } = useValidator();
  const {
    articleCategories,
    fetchArticleCategories,
  } = useArticles();
  const { fetchClients, clients } = useAdvice();
  const defaultTime = channel !== OrderLineChannel.RECEPTION
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ? (vigilHour as any)
    : formatHour(getCurrentDate());
  const [defaultValues, setDefaultValues] = useState<OrderLine
  & {idSupplier?: string;}>({
    amount: 1,
    articleDescription: value?.articleDescription || '',
    category: value?.category || '',
    channel: Object
      .values(OrderLineChannel)
      .find((channelOption) => channelOption as string === channel as string)
      || OrderLineChannel.PROCESSING,
    client: {
      identifier: value?.client?.identifier || '',
    },
    code: value?.code || '',
    comments: value?.comments || '',
    date: value?.date || (channel !== OrderLineChannel.RECEPTION
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ? (vigilDate as any)
      : getCurrentDate()),
    dedicatory: value?.dedicatory || '',
    erpId: Number(erpId),
    idSupplier: value?.idSupplier || '',
    observations: value?.observations || '',
    price: value?.price,
    supplier: value?.supplier || '',
    time: defaultTime,
  });

  useEffect(() => {
    if (clients?.length === 1 && !defaultValues.client?.identifier) {
      setDefaultValues({
        ...defaultValues,
        client: {
          identifier: clients[0].code || '',
        },
      });
    }
  }, [clients, defaultValues]);

  useEffect(
    () => {
      onChange(defaultValues);
      fetchArticleCategories()
        .catch(() => false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(
    () => {
      if (erpId) {
        fetchClients(Number(id));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [erpId],
  );
  return (
    <Formik
      initialValues={defaultValues}
      validate={(values) => {
        const recordArticleRequest = {
          ...values,
          date: values.date ? new Date(values.date) : undefined,
          dedicatory: values.dedicatory,
          erpId: values.erpId,
          idSupplier: values?.idSupplier || '',
          observations: values.observations,
          paymentLocation: values.paymentLocation,
          price: values.price
            ? parseFloat(values.price?.toString().replace(',', '.'))
            : undefined,
          supplier: values?.supplier || '',
          time: values.time,
        };
        onChange({
          ...recordArticleRequest,
          articleProviders: undefined,
          isCalendarOpen: undefined,
          isProviderOpen: undefined,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any);

        return isGift
          ? validateArticleGift(recordArticleRequest)
          : validateArticleAddRequest(recordArticleRequest);
      }}
      enableReinitialize
      validateOnBlur
      validateOnChange
      validateOnMount
      onSubmit={() => Promise.resolve()}
    >
      {({
        setFieldValue, values, errors, isValid,
      }: FormikProps<OrderLine & {
        articleDescription?: string;
        supplierDescription?: string;
        idSupplier?: string;
        isCalendarOpen?: boolean;
        isProviderOpen?: boolean;
      }>) => {
        onIsValidUpdate?.(isValid);

        return (
          <Form autoComplete="off" className={classNames(className, { 'is-calendar-open': values.isCalendarOpen, 'is-provider-open': values.isProviderOpen })}>
            {showClientSelector && (
            <Select<MasterDataDto>
              key="client.identifier"
              disabled={clientDisabled}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              error={(errors.client as any)?.identifier}
              getLabel={({ description }) => description || ''}
              getValue={({ code }) => code || ''}
              name="client.identifier"
              options={clients}
              placeholder={t('common.client')}
              value={clients.find((client) => client.code === values.client?.identifier)}
              searchable
              onChange={(client) => {
                setFieldValue('client.identifier', client?.code);
              }}
            />
            )}
            {showCategorySelector && (
            <Select<MasterDataDto>
              key="category"
              disabled={articleTypeDisabled}
              error={errors.category}
              getLabel={({ description }) => description || ''}
              getValue={({ code }) => code || ''}
              name="category"
              options={articleCategories}
              placeholder={t('article.flowers.category')}
              value={articleCategories.find((category) => category.code === values.category)}
              searchable
              onChange={(category) => {
                setFieldValue('category', category?.code);
              }}
            />
            )}
            {showArticleDescription && (<div>{value?.articleDescription}</div>)}
            {showArticleSelector && (
              <AddArticleRequestFormArticleSelector
                category={values.category}
                disabled={articleDisabled}
                onArticleChange={onArticleChange}
              />

            )}
            {values.code && (
            <Input
              disabled={priceDisabled}
              error={errors.price}
              id="price"
              name="price"
              placeholder={t('article.flowers.price')}
              type="text"
              value={values.price !== undefined && values.price !== null
                ? priceFormatWithoutCurrency(values.price) : ''}
              onBlur={() => false}
              onChange={(amount) => { setFieldValue('price', amount); }}
            />
            )}
            <Input
              error={errors.observations}
              id="observations"
              name="observations"
              placeholder={t('article.flowers.observations')}
              type="text"
              value={values.observations || ''}
              onBlur={() => false}
              onChange={(text) => setFieldValue('observations', text)}
            />
            <Input
              error={errors.dedicatory}
              id="dedicatory"
              name="dedicatory"
              placeholder={t('article.flowers.dedicatory')}
              type="text"
              value={values.dedicatory || ''}
              onBlur={() => false}
              onChange={(text) => setFieldValue('dedicatory', text)}
            />
            <Datepicker
              key="date"
              disabled={channel === OrderLineChannel.RECEPTION}
              error={errors.date}
              name="date"
              placeholder={t('article.flowers.date')}
              selected={values.date}
              onCalendarClose={() => {
                setFieldValue('isCalendarOpen', false);
              }}
              onCalendarOpen={() => {
                setFieldValue('isCalendarOpen', true);
              }}
              onChange={(date) => setFieldValue('date', date)}
            />
            <Datepicker
              key="time"
              disabled={channel === OrderLineChannel.RECEPTION}
              error={errors.time}
              name="time"
              placeholder={t('article.flowers.time')}
              selected={values.time}
              showTimeSelectOnly
              onChange={(time) => setFieldValue('time', time)}
            />
            <AddArticleRequestFormArticleProviderSelector
              articleCode={values.code}
              disabled={providerDisabled}
              onSupplierChange={onSupplierChange}
            />
            <Input
              error={errors.comments}
              id="comments"
              name="comments"
              placeholder={t('common.comments')}
              type="text"
              value={values.comments || ''}
              onBlur={() => false}
              onChange={(text) => setFieldValue('comments', text)}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
